/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useArticlesByGroupId } from "../hooks/useArticlesByGroupId";
import { ArticleViewSkeleton } from "../Skeletons/ArticleViewSkeleton";
import { useInputPopup } from "../../InputPopup";
import { Trans, useTranslation } from "@coworker/locales";
import {
  ArticleListItemMobile,
  CenterContainer,
  CenterContainerWithGap,
  ListWrapper,
  SizedText,
} from "../styles/styles";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { FullWidthButton } from "../../SkapaButton";
import { ModalTypes } from "../types/views";
import { useQueryClient } from "@tanstack/react-query";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import { GroupActionsModal } from "../Organisms/Modals/GroupActionModal";
import { Group } from "../types/groups";
import { EditGroupModal } from "../Organisms/Modals/EditGroupModal";
import { useGroupsByMediaId } from "../hooks/useGroupsByMediaId";
import { DeleteGroupModal } from "../Organisms/Modals/DeleteGroupModal";
import { QueryKeys } from "../hooks/queryKeys";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import { useMediaById } from "../hooks/useMediaById";
import { sortArticlesByItemName } from "../../../helpers/sorters";
import { useTasksByMedia } from "../hooks/useTasksByMedia";
import { joinProductArticleIds } from "../tools";
import { useEDSData } from "../hooks/useEDSData";
import { useSalesStopData } from "../hooks/useSalesStopData";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import {
  isLocalCombinationArticle,
  processArticles,
  selectedArticleIdsToArticles,
} from "../Helpers/articleHelpers";
import { ArticleType } from "@coworker/types/lib/tasks/constants";
import { LocalCombinationItemsListView } from "@coworker/apprestructured/src/media-maintenance/components/LocalCombinations/LocalCombinationItemsListView/LocalCombinationItemsListView";
import { ItemListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/ItemListViewItem/ItemListViewItem";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import styled from "styled-components";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { SelectArticles } from "../Molecules/SelectArticles";
import { ArticleIdNbrArticles, Articles } from "../types/article";
import { selectArticleIdNbrArticles } from "../Molecules/MediaArticles.helper";
import { PleaseFillIn } from "../../Reusable/PleaseFillIn";
import { MediaArticlesModals } from "../Organisms/Modals/MediaArticlesModals";
import { MediaResponse } from "../types/media";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { useItemsInfoBatch } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { SearchBar } from "../Molecules/SearchBar";
import {
  AggregatedArticle,
  aggregateArticles,
} from "../hooks/useAggregatedArticles";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { FilterPanelMobile } from "@coworker/apprestructured/src/shared/filters/components/FilterPanelMobile";
import { useGroupMediaArticlesFilters } from "../hooks/useMediaArticlesFilters";
import { useCombinedArticles } from "../hooks/useCombinedArticles";
import { MediaImagesCarousel } from "../Molecules/MediaImagesCarousel";

const ArticleFiltersContainer = styled("div")`
  border-bottom: 1px solid var(--grey200);
`;
const SearchBarContainer = styled("div")`
  padding: 16px;
`;
const SelectButtonContainer = styled("div")`
  padding: 16px;
  text-align: center;
  position: sticky;
  top: 56px;
  background-color: white;
  z-index: 1;
`;

export function GroupView() {
  const { groupId } = useParams();
  const [queryParams] = useSearchParams();
  const { t } = useTranslation();
  const { getInput } = useInputPopup();
  const queryClient = useQueryClient();
  const { showToast } = useToastNotification();
  const navigate = useNavigate();

  const mediaId = queryParams.get("mediaId") ?? undefined;

  const { data: articles } = useArticlesByGroupId(groupId);
  const { data: groups } = useGroupsByMediaId(mediaId);
  const { data: mediaResponse } = useMediaById(mediaId);
  const articleNumbers = joinProductArticleIds(articles ?? []);
  const storeId = useStoreId();
  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, articleNumbers);
  const { data: salesStopData } = useSalesStopData(storeId, articleNumbers);
  const { taskLinks } = useTasksByMedia(mediaId);

  const [currentModalType, setCurrentModalType] = useState<ModalTypes>(
    ModalTypes.DEFAULT_VIEW
  );

  const [isAddingArticles, setIsAddingArticles] = useState(false);
  const [isSelectArticlesMode, setIsSelectArticlesMode] = useState(false);
  const [selectedArticleIdsNbrArticles, setSelectedArticleIdsNbrArticles] =
    useState<ArticleIdNbrArticles[]>([]);
  const [warningText, setWarningText] = useState("");

  const [selectedArticles, setSelectedArticles] = useState<Articles>([]);
  const [isSearchedResult, setIsSearchedResult] = useState(false);
  const [articleSearchResult, setArticleSearchResult] = useState<
    AggregatedArticle[]
  >([]);

  const allArticlesInMedia =
    useCombinedArticles(
      countryId,
      mediaResponse?.media.storeId ?? "",
      mediaId ?? "",
      articles
    ) ?? [];

  // Process articles to separate active and combination articles
  const { activeArticles, groupedCombinationArticles } = useMemo(
    () => processArticles(allArticlesInMedia),
    [allArticlesInMedia]
  );

  const uniqueCombinationArticles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(groupedCombinationArticles ?? {}).map(([key, arts]) => [
          key,
          aggregateArticles(arts),
        ])
      ),
    [groupedCombinationArticles]
  );

  const articlesToUse = isSearchedResult ? articleSearchResult : activeArticles;
  const {
    useFiltersFactory,
    filterValues,
    setFilterValues,
    useFilteredArticleData,
  } = useGroupMediaArticlesFilters(articlesToUse as AggregatedArticle[]);
  const articlesToShow =
    useFilteredArticleData(articlesToUse as AggregatedArticle[]) ?? [];

  // Merge articles and sub-articles for searching, and derive parentArticle immutably.
  const searchableArticles = useMemo(() => {
    const merged = [
      ...Object.values(uniqueCombinationArticles).flatMap((x) => x),
      ...activeArticles,
    ];

    return merged.map((article) => {
      if (!article.parentArticleCombinationNumber) {
        return article;
      }

      const parentArticle = merged.find(
        (a) =>
          a.productArticleId === article.parentArticleCombinationNumber &&
          a.groupId === article.groupId
      );

      if (!parentArticle) {
        return article;
      }

      return {
        ...article,
        parentArticle,
      };
    });
  }, [uniqueCombinationArticles, activeArticles]);
  const group = groups?.find((group) => group.id === groupId);

  const previousPath = useMemo(
    () => `/mediamaintenance/tabs/${mediaResponse?.media.id}?tab=groups-tab`,
    [mediaResponse?.media.id]
  );

  const linkbar = useMemo(() => {
    if (
      !group?.name ||
      !mediaResponse?.media.id ||
      !mediaResponse?.media.name
    ) {
      return <LinkbarLoading />;
    }

    return (
      <Linkbar
        currentLabel={group.name}
        items={[
          {
            label: t("mediaMaintenanceString"),
            to: "/mediamaintenance",
          },
          {
            label: mediaResponse.media.name,
            to: previousPath,
          },
        ]}
      />
    );
  }, [group?.name, mediaResponse?.media.id, mediaResponse?.media.name, t]);

  const itemNos = useMemo(
    () => articles?.map((a) => a.productArticleId).filter(Boolean),
    [articles]
  );
  const { data: itemsInfo, isLoading } = useItemsInfoBatch(itemNos);
  const itemInfoMap = useMemo(() => {
    const map = new Map<string, Item>();

    itemsInfo?.forEach((item) => {
      map.set(item.no, item);
    });

    return map;
  }, [itemsInfo]);
  const fullScreenPopup = (children: React.ReactNode) => (
    <FullScreenPopup
      appBarConfig={{
        title: group?.name ?? t("loadingString"),
        actions: [
          {
            name: "settings",
            icon: <FixaSSRIcon paths={PencilIconPath} />,
            onClick: () => setCurrentModalType(ModalTypes.GROUP_ACTIONS),
            position: "right",
          },
        ],
      }}
      actionBarContent={
        <CenterContainerWithGap $centerText={true}>
          {isSelectArticlesMode ? (
            <>
              <PleaseFillIn
                show={!!warningText}
                alternateWarningTranslation={warningText}
                centerText={true}
              />
              <FullWidthButton
                text={t("continue2String")}
                onClick={async () => {
                  if (selectedArticleIdsNbrArticles.length > 0) {
                    setCurrentModalType(
                      ModalTypes.GROUP_SELECTED_ARTICLES_ACTIONS
                    );
                    setSelectedArticles(
                      selectedArticleIdsToArticles(
                        activeArticles,
                        selectedArticleIdsNbrArticles,
                        Object.values(groupedCombinationArticles).flat()
                      )
                    );
                  } else {
                    setWarningText(t("selectArticlesToContinueString"));
                  }
                }}
                type={"primary"}
              />
            </>
          ) : (
            <FullWidthButton
              type="primary"
              text={t("addArticlesToGroupString")}
              onClick={async () => {
                await getInput("addMultipleArticles", {
                  mediaId,
                  groupId,
                  setIsAddingArticles,
                });
                await queryClient.invalidateQueries({
                  queryKey: [QueryKeys.ArticlesByGroup, { id: groupId }],
                });
              }}
            />
          )}
        </CenterContainerWithGap>
      }
      noPadding
      fullHeightContent
      linkbar={linkbar}
    >
      <>
        {children}
        <GroupActionsModal
          modalVisibility={currentModalType === ModalTypes.GROUP_ACTIONS}
          onTriggerNewModal={(modalType: ModalTypes) => {
            setCurrentModalType(modalType);
          }}
          mediaId={mediaId ?? ""}
          currentGroup={group as Group}
          newGroupEditModal={true}
        />
        <EditGroupModal
          modalVisibility={currentModalType === ModalTypes.EDIT_GROUP_NAME}
          currentGroup={group as Group}
          onTriggerNewModal={(modalType: ModalTypes) => {
            setCurrentModalType(modalType);
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.GroupsByMedia, mediaId],
            });
          }}
          groups={groups || []}
        />
        <DeleteGroupModal
          modalVisibility={currentModalType === ModalTypes.DELETE_GROUP}
          mediaId={mediaId || ""}
          allArticles={articles || []}
          currentGroup={group}
          onTriggerNewModal={(_modalType: ModalTypes) => {
            navigate(previousPath, { replace: true });
          }}
        />
        {currentModalType !== ModalTypes.DEFAULT_VIEW && (
          <MediaArticlesModals
            selectedArticles={selectedArticles}
            allArticles={activeArticles || []}
            currentModalType={currentModalType}
            onModalTypeChange={(modalType: ModalTypes) => {
              if (modalType === ModalTypes.DEFAULT_VIEW) {
                setIsSelectArticlesMode(false);
                setSelectedArticles([]);
                setSelectedArticleIdsNbrArticles([]);
              }
              setWarningText("");
              setCurrentModalType(modalType);
            }}
            groups={groups || []}
            storeId={mediaResponse?.media.storeId ?? ""}
            mediaId={mediaResponse?.media.id ?? ""}
            currentGroup={group as Group}
            media={mediaResponse as MediaResponse}
          />
        )}
      </>
    </FullScreenPopup>
  );

  const articlesWasAdded = useRef(isAddingArticles);
  useEffect(() => {
    if (articlesWasAdded.current && !isAddingArticles) {
      showToast(t("articlesAddedSuccessfullyString"), undefined, undefined, 2);
    }
    articlesWasAdded.current = isAddingArticles;
  }, [isAddingArticles]);

  if (!articles || !groups) {
    return <>{fullScreenPopup(<ArticleViewSkeleton />)}</>;
  }

  if (isAddingArticles) {
    return (
      <FullScreenPopup
        noPadding
        linkbar={linkbar}
        appBarConfig={{
          title: group?.name ?? t("loadingString"),
          actions: [
            {
              name: "settings",
              icon: <FixaSSRIcon paths={PencilIconPath} />,
              onClick: () => {},
              position: "right",
            },
          ],
        }}
      >
        {[...Array(3)].map((_, index) => (
          <SimpleArticleCardSkeleton key={index} />
        ))}
      </FullScreenPopup>
    );
  }

  if (!articles || articles?.length === 0) {
    return (
      <>
        {fullScreenPopup(
          <CenterContainer $centerText={true} style={{ margin: "48px 0px" }}>
            <SizedText $fontSize={20} $bold={true}>
              <Trans>noArticlesInGroupString</Trans>
            </SizedText>
          </CenterContainer>
        )}
      </>
    );
  }

  return (
    <>
      {mediaResponse &&
        fullScreenPopup(
          <>
            <MediaImagesCarousel
              media={mediaResponse.media}
              groupId={group?.id}
            />
            <ArticleFiltersContainer>
              <FilterPanelMobile
                useFiltersFactory={useFiltersFactory}
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                feature="media-articles"
              />
            </ArticleFiltersContainer>
            <SearchBarContainer>
              <SearchBar
                articles={(searchableArticles as AggregatedArticle[]) ?? []}
                onSearchResults={(
                  isSearchResult: boolean,
                  result: AggregatedArticle[]
                ) => {
                  setIsSearchedResult(isSearchResult);
                  setArticleSearchResult(result);
                }}
              />
            </SearchBarContainer>
            <SelectButtonContainer>
              <FixaButton
                type={"secondary"}
                text={
                  isSelectArticlesMode
                    ? t("cancelString")
                    : t("selectArticlesString")
                }
                onClick={() => {
                  setIsSelectArticlesMode((prev) => !prev);
                  setSelectedArticleIdsNbrArticles([]);
                  setWarningText("");
                }}
                fluid
              />
            </SelectButtonContainer>
            <SelectArticles
              selectedArticles={selectedArticleIdsNbrArticles}
              uniqueArticles={activeArticles}
              filteredArticles={articlesToShow}
              isSelected={isSelectArticlesMode}
              onSelectAll={(selectAllChecked: boolean) => {
                if (selectAllChecked) {
                  setSelectedArticleIdsNbrArticles(
                    activeArticles?.map((article) => ({
                      articleId: article.id,
                      nbrArticles: article.nbrArticles,
                      productArticleId: article.productArticleId,
                    })) ?? []
                  );
                } else {
                  setSelectedArticleIdsNbrArticles([]);
                }
              }}
            />
            <ListWrapper>
              {articlesToShow.sort(sortArticlesByItemName).map((article) => (
                <>
                  <ArticleListItemMobile key={article.id}>
                    <ItemListViewItem
                      article={article}
                      itemCardRenderType={ItemCardRenderType.link}
                      media={mediaResponse?.media}
                      taskLinks={taskLinks}
                      edsData={
                        edsData?.find(
                          (eds: { productArticleId: string }) =>
                            eds.productArticleId === article.productArticleId
                        ) ?? null
                      }
                      salesStopData={
                        salesStopData?.find(
                          (salesStop) =>
                            salesStop.productArticleId ===
                            article.productArticleId
                        ) ?? null
                      }
                      selectArticleIdsMode={isSelectArticlesMode}
                      handleSelectedItems={(articleIdNbrArticles) =>
                        selectArticleIdNbrArticles(
                          articleIdNbrArticles,
                          selectedArticleIdsNbrArticles,
                          setSelectedArticleIdsNbrArticles
                        )
                      }
                      isSelected={
                        !!selectedArticleIdsNbrArticles.find(
                          (item) => item.articleId === article.id
                        )
                      }
                      itemInfo={
                        itemInfoMap.get(article.productArticleId) ?? null
                      }
                      itemInfoLoading={isLoading}
                      mediaItemType={
                        isSearchedResult
                          ? article.parentArticleCombinationNumber
                            ? MediaItemType.LC_SUBITEM
                            : MediaItemType.REGULAR_ITEM
                          : MediaItemType.REGULAR_ITEM
                      }
                    />
                  </ArticleListItemMobile>
                  {isLocalCombinationArticle(
                    article.productArticleType as ArticleType
                  ) && (
                    <LocalCombinationItemsListView
                      parentArticle={article}
                      combinationArticles={groupedCombinationArticles}
                      edsData={edsData ?? null}
                      salesStopData={salesStopData}
                      mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
                    />
                  )}
                </>
              ))}
            </ListWrapper>
          </>
        )}
    </>
  );
}
